import {useTranslation} from 'react-i18next';

import {Box, Button, Typography} from '@mui/material';

import {greenColors} from '../../layouts/theme/designSystem';

interface Props {
  page: number;
  totalItems: number;
  itemsPerPage: number;
  translationKeyPrefix: string;
  onPageChange: (page: number) => void;
}

export const TablePaginator = ({page, totalItems, itemsPerPage, translationKeyPrefix, onPageChange}: Props) => {
  const {t} = useTranslation('translation', {keyPrefix: translationKeyPrefix});

  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  const safePage = Math.min(page, totalPages);
  const start = totalItems === 0 ? 0 : (safePage - 1) * itemsPerPage + 1;
  const end = Math.min(safePage * itemsPerPage, totalItems);

  const goPrev = () => onPageChange(Math.max(1, safePage - 1));
  const goNext = () => onPageChange(Math.min(totalPages, safePage + 1));

  const visiblePageNumbers = (): number[] => {
    if (totalPages <= 3) {
      return Array.from({length: totalPages}, (_, i) => i + 1);
    }
    if (safePage <= 2) {
      return [1, 2, 3];
    }
    if (safePage >= totalPages - 1) {
      return [totalPages - 2, totalPages - 1, totalPages];
    }
    return [safePage - 1, safePage, safePage + 1];
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: {xs: 'column', sm: 'row'},
        alignItems: {sm: 'center'},
        justifyContent: 'space-between',
        gap: 2,
        pt: 2,
        pb: 0,
        width: '100%',
      }}
    >
      <Typography sx={{fontSize: '14px', lineHeight: 1.5, color: 'text.primary'}}>
        {t('showingRange', {start, end, total: totalItems})}
      </Typography>
      <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap'}}>
        <Button
          variant='outlined'
          onClick={goPrev}
          disabled={safePage <= 1}
          sx={{
            minWidth: 86,
            height: 28,
            borderRadius: '5px',
            fontSize: '14px',
            lineHeight: 1.5,
            textTransform: 'none',
            borderColor: greenColors[700],
            color: greenColors[700],
          }}
        >
          {t('prev')}
        </Button>
        {visiblePageNumbers().map((n) => (
          <Button
            key={n}
            variant='outlined'
            onClick={() => onPageChange(n)}
            sx={{
              minWidth: 28,
              width: 28,
              height: 28,
              p: 0,
              borderRadius: '5px',
              fontSize: '14px',
              textTransform: 'none',
              borderColor: greenColors[700],
              color: greenColors[700],
            }}
          >
            {n}
          </Button>
        ))}
        <Button
          variant='outlined'
          onClick={goNext}
          disabled={safePage >= totalPages}
          sx={{
            minWidth: 76,
            height: 28,
            borderRadius: '5px',
            fontSize: '14px',
            lineHeight: 1.5,
            textTransform: 'none',
            borderColor: greenColors[700],
            color: greenColors[700],
          }}
        >
          {t('next')}
        </Button>
      </Box>
    </Box>
  );
};
